import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { useSortableData } from "@/hooks/useSortableData";
import { useClientPaginatedData } from "@/hooks/usePaginatedData";
import {
  Play,
  Plus,
  Edit,
  RefreshCw,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  GitBranch,
  Zap,
  Settings,
  Pause,
  Loader2,
  Eye,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  DollarSign,
  Hash,
  Bot,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { automationApi, WorkflowExecution } from "@/services/api/automation";
import {
  WorkflowFlowDialog,
  type FlowMode,
  type WorkflowStepData,
} from "@/components/portal/workflow-flow";
import { toast } from "sonner";

export const Route = createFileRoute("/portal/automation/workflows")({
  component: Workflows,
});

interface Workflow {
  id: string;
  name: string;
  description: string;
  type: string;
  trigger: string;
  status: string;
  last_run?: string;
  success_rate?: number;
  template_id?: string;
  version?: string;
  required_agents?: string[];
  required_service_actions?: string[];
}

interface WorkflowStep {
  step_number: number;
  agent_name: string;
  action_type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  cost_estimate?: number;
  error_message?: string;
  action_data?: Record<string, unknown>;
  result?: Record<string, unknown>;
}

interface WorkflowExecutionDetail {
  workflow_execution_id: string;
  entity_id: string;
  entity_type: string;
  service_name: string;
  started_at: string;
  completed_at?: string;
  total_duration_ms: number;
  total_tokens: number;
  total_cost: number;
  step_count: number;
  status: string;
  steps: WorkflowStep[];
}

function Workflows() {
  const { token } = useAuth();
  const { can } = usePermissions();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId;
  const canCreateWorkflows = can("create", "automation");
  const canEditWorkflows = can("edit", "automation");
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    null,
  );
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [executeForm, setExecuteForm] = useState({
    workflow_name: "",
    entity_id: "",
    entity_type: "ticket",
  });
  const [executionFilter, setExecutionFilter] = useState({
    workflow_type: "all",
    status: "all",
  });
  const [executionSearch, setExecutionSearch] = useState("");

  // Execution detail dialog state
  const [showExecutionDetail, setShowExecutionDetail] = useState(false);
  const [selectedExecution, setSelectedExecution] =
    useState<WorkflowExecution | null>(null);
  const [executionDetail, setExecutionDetail] =
    useState<WorkflowExecutionDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Flow visualization state
  const [showFlowDialog, setShowFlowDialog] = useState(false);
  const [flowMode, setFlowMode] = useState<FlowMode>("template");
  const [flowTemplateId, setFlowTemplateId] = useState<string | null>(null);
  const [flowExecutionSteps, setFlowExecutionSteps] = useState<
    WorkflowStepData[]
  >([]);
  const [flowExecutionStatus, setFlowExecutionStatus] = useState<string>("");

  const loadWorkflows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Fetch workflow templates from database
      const templatesResponse = await automationApi.workflows.listTemplates(
        token,
        {
          is_active: "true",
        },
      );

      // Transform database templates into workflow cards
      const workflowsFromDb = (templatesResponse as unknown[]).map(
        (template: unknown) => {
          const t = template as Record<string, unknown>;
          return {
            id: t.id as string,
            name: (t.name as string) || (t.template_id as string),
            description:
              (t.description as string) || `${t.workflow_type} workflow`,
            type: t.workflow_type as string,
            trigger: "api", // Could be determined from workflow config
            status: t.is_active ? "active" : "inactive",
            last_run: t.updated_at as string,
            success_rate: 0, // Would be calculated from execution history
            template_id: t.template_id as string,
            version: t.version as string,
            required_agents: (t.required_agents as string[]) || [],
            required_service_actions:
              (t.required_service_actions as string[]) || [],
          };
        },
      );

      setWorkflows(workflowsFromDb);

      // Also load execution history for success rates
      try {
        const summaryResponse =
          await automationApi.workflows.getExecutionSummary(token, 30);
        const summary = summaryResponse as Record<string, unknown>;
        const executionStats =
          (summary.most_used_templates as Record<string, unknown>[]) || [];

        // Update workflows with execution stats
        setWorkflows((prev) =>
          prev.map((workflow) => {
            const stats = executionStats.find(
              (s: Record<string, unknown>) =>
                s.template_id === workflow.template_id,
            );
            if (stats) {
              return {
                ...workflow,
                success_rate: (stats.success_rate as number) || 0,
                last_run: (stats.last_execution as string) || workflow.last_run,
              };
            }
            return workflow;
          }),
        );
      } catch (err) {
        console.warn("Failed to load execution stats:", err);
      }
    } catch (error) {
      console.error("Failed to load workflows:", error);
      toast.error("Failed to load workflows");
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadExecutions = useCallback(async () => {
    if (!token) return;
    try {
      const response = await automationApi.workflows.listExecutions(token);
      // Transform the API response to match the expected format
      const transformedExecutions = (response as unknown[]).map(
        (exec: unknown) => {
          const e = exec as Record<string, unknown>;
          return {
            id: e.execution_id as string,
            organisation_id: "org1", // This will come from context
            workflow_name: (e.template_id as string) || "Unnamed Workflow",
            entity_id: e.execution_id as string,
            entity_type: "workflow",
            status: e.status as "running" | "completed" | "failed",
            started_at: e.start_time as string,
            completed_at: e.end_time as string | undefined,
            result: {
              total_steps: e.total_steps as number,
              completed_steps: e.completed_steps as number,
              failed_steps: e.failed_steps as number,
              total_tokens: e.total_tokens as number,
              total_cost: e.total_cost as number,
              agents_used: e.agents_used as string[],
            },
          };
        },
      );
      setExecutions(transformedExecutions);
    } catch (error) {
      console.error("Failed to load executions:", error);
      setExecutions([]);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadWorkflows();
      loadExecutions();
    }
  }, [token, organisationId, loadWorkflows, loadExecutions]);

  const executeWorkflow = async () => {
    if (!token || !executeForm.entity_id || !executeForm.workflow_name) return;
    try {
      setLoading(true);
      await automationApi.workflows.executeWorkflow(
        token,
        executeForm.workflow_name,
        executeForm.entity_id,
        executeForm.entity_type,
      );
      toast.success("Workflow execution started");
      setShowExecuteDialog(false);
      setExecuteForm({
        workflow_name: "",
        entity_id: "",
        entity_type: "ticket",
      });
      loadExecutions();
    } catch (error) {
      console.error("Failed to execute workflow:", error);
      toast.error("Failed to start workflow execution");
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = (workflow: Workflow) => {
    setExecuteForm({
      ...executeForm,
      workflow_name: workflow.name,
    });
    setShowExecuteDialog(true);
  };

  const loadExecutionDetail = async (execution: WorkflowExecution) => {
    if (!token) return;
    setSelectedExecution(execution);
    setShowExecutionDetail(true);
    setLoadingDetail(true);
    setCurrentStepIndex(0);

    try {
      const detail = await automationApi.audit.getWorkflowDetails(
        token,
        execution.id,
      );
      setExecutionDetail(detail as WorkflowExecutionDetail);
    } catch (error) {
      console.error("Failed to load execution detail:", error);
      toast.error("Failed to load execution details");
      setExecutionDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const currentStep = executionDetail?.steps?.[currentStepIndex] ?? null;
  const totalSteps = executionDetail?.steps?.length ?? 0;

  // Filter executions based on selected dropdown filters
  const filteredExecutions = executions.filter((execution) => {
    if (
      executionFilter.status !== "all" &&
      execution.status !== executionFilter.status
    ) {
      return false;
    }
    if (
      executionFilter.workflow_type !== "all" &&
      execution.workflow_name !== executionFilter.workflow_type
    ) {
      return false;
    }
    return true;
  });

  // Get unique workflow types from executions for filter dropdown
  const workflowTypes = Array.from(
    new Set(executions.map((e) => e.workflow_name)),
  );

  // Sort + paginate executions
  const { sortedData: sortedExecutions, requestSort: requestExecSort, getSortDirection: getExecSortDirection } =
    useSortableData(filteredExecutions, {
      initialSort: { key: "started_at", direction: "desc" },
    });

  const {
    paginatedData: paginatedExecutions,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems: totalExecutions,
    startIndex: execStartIndex,
    endIndex: execEndIndex,
  } = useClientPaginatedData({
    data: sortedExecutions as unknown as Record<string, unknown>[],
    itemsPerPage: 25,
    searchFields: ["workflow_name", "entity_type", "entity_id"],
    searchTerm: executionSearch,
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { className: string; icon: typeof Clock }> =
      {
        running: { className: "", icon: Clock },
        completed: {
          className: "bg-green-100 text-green-800",
          icon: CheckCircle,
        },
        failed: { className: "bg-red-100 text-red-800", icon: XCircle },
        active: { className: "bg-green-100 text-green-800", icon: CheckCircle },
        inactive: { className: "", icon: Pause },
      };
    const config = variants[status] || { className: "", icon: Clock };
    const Icon = config.icon;
    return (
      <Badge variant="secondary" className={`gap-1 ${config.className}`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const getTriggerIcon = (trigger: string) => {
    const icons: Record<string, typeof Play> = {
      manual: Play,
      schedule: Clock,
      webhook: Zap,
      api: GitBranch,
      event: Activity,
    };
    const Icon = icons[trigger] || Activity;
    return <Icon className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Workflows</h1>
          <p className="mt-2 text-lg text-slate-600">
            Design and manage automation workflows
          </p>
        </div>
        <div className="flex gap-2">
          {canCreateWorkflows && (
            <Button className="rounded-full" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Workflow
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="workflows">
        <TabsList>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="executions">Executions</TabsTrigger>
        </TabsList>

        <TabsContent value="workflows" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : workflows.length > 0 ? (
            <div className="grid gap-4">
              {workflows.map((workflow) => (
                <Card key={workflow.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{workflow.name}</CardTitle>
                        <CardDescription>
                          {workflow.description}
                        </CardDescription>
                      </div>
                      {getStatusBadge(workflow.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          {getTriggerIcon(workflow.trigger)}
                          <span>Trigger: {workflow.trigger}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span>Type: {workflow.type}</span>
                        </div>
                        {workflow.success_rate !== undefined && (
                          <div className="flex items-center gap-2">
                            <Activity className="h-4 w-4" />
                            <span>Success Rate: {workflow.success_rate}%</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExecute(workflow)}
                        >
                          <Play className="mr-2 h-4 w-4" />
                          Execute
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFlowTemplateId(
                              workflow.template_id || workflow.id,
                            );
                            setFlowMode("template");
                            setShowFlowDialog(true);
                          }}
                        >
                          <GitBranch className="mr-2 h-4 w-4" />
                          View Flow
                        </Button>
                        {canEditWorkflows && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedWorkflow(workflow);
                                setShowEditDialog(true);
                              }}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedWorkflow(workflow);
                                setShowConfigDialog(true);
                              }}
                            >
                              <Settings className="mr-2 h-4 w-4" />
                              Configure
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Activity className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No workflows yet</h3>
                <p className="text-muted-foreground text-center mb-4">
                  {canCreateWorkflows
                    ? "Create your first workflow to start automating tasks"
                    : "No workflows have been configured yet"}
                </p>
                {canCreateWorkflows && (
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Workflow
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="executions" className="space-y-4">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <SearchInput
                value={executionSearch}
                onChange={(e) => setExecutionSearch(e.target.value)}
                onClear={() => setExecutionSearch("")}
                placeholder="Search executions..."
                containerClassName="flex-1 min-w-[200px]"
              />
              <Select
                value={executionFilter.workflow_type}
                onValueChange={(value) =>
                  setExecutionFilter({ ...executionFilter, workflow_type: value })
                }
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Workflows" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Workflows</SelectItem>
                  {workflowTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={executionFilter.status}
                onValueChange={(value) =>
                  setExecutionFilter({ ...executionFilter, status: value })
                }
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={loadExecutions}>
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Refresh
              </Button>
            </div>

            {(paginatedExecutions as unknown as WorkflowExecution[]).length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead direction={getExecSortDirection("workflow_name")} onSort={() => requestExecSort("workflow_name")}>Workflow</SortableTableHead>
                      <SortableTableHead direction={null} onSort={() => {}} disabled>Entity</SortableTableHead>
                      <SortableTableHead direction={getExecSortDirection("started_at")} onSort={() => requestExecSort("started_at")}>Started</SortableTableHead>
                      <SortableTableHead direction={null} onSort={() => {}} disabled>Duration</SortableTableHead>
                      <SortableTableHead direction={getExecSortDirection("status")} onSort={() => requestExecSort("status")}>Status</SortableTableHead>
                      <SortableTableHead direction={null} onSort={() => {}} disabled>Details</SortableTableHead>
                      <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">{" "}</SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(paginatedExecutions as unknown as WorkflowExecution[]).map((execution) => {
                      const duration = execution.completed_at
                        ? (
                            (new Date(execution.completed_at).getTime() -
                              new Date(execution.started_at).getTime()) /
                            1000
                          ).toFixed(1)
                        : "-";
                      return (
                        <TableRow key={execution.id}>
                          <TableCell className="font-medium">
                            {execution.workflow_name}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {execution.entity_type}:{" "}
                              {execution.entity_id.substring(0, 8)}...
                            </span>
                          </TableCell>
                          <TableCell>
                            {new Date(execution.started_at).toLocaleString()}
                          </TableCell>
                          <TableCell>{duration}s</TableCell>
                          <TableCell>
                            {getStatusBadge(execution.status)}
                          </TableCell>
                          <TableCell>
                            {execution.result && (
                              <div className="text-sm text-muted-foreground">
                                Steps:{" "}
                                {String(
                                  (execution.result as Record<string, unknown>).completed_steps ?? 0,
                                )}
                                /
                                {String(
                                  (execution.result as Record<string, unknown>).total_steps ?? 0,
                                )}
                                {Number(
                                  (execution.result as Record<string, unknown>).total_tokens ?? 0,
                                ) > 0 && (
                                  <span className="ml-2">
                                    Tokens:{" "}
                                    {String(
                                      (execution.result as Record<string, unknown>).total_tokens,
                                    )}
                                  </span>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => loadExecutionDetail(execution)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {totalExecutions > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <Pagination
                      page={currentPage}
                      totalPages={totalPages}
                      totalCount={totalExecutions}
                      pageSize={itemsPerPage}
                      pageSizeOptions={[10, 25, 50]}
                      startIndex={execStartIndex}
                      endIndex={execEndIndex}
                      canGoPrev={currentPage > 1}
                      canGoNext={currentPage < totalPages}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setItemsPerPage}
                      onFirstPage={() => setCurrentPage(1)}
                      onLastPage={() => setCurrentPage(totalPages)}
                      onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  {executionSearch ? "No executions match your search" : "No executions yet"}
                </h3>
                <p className="text-muted-foreground">
                  {executionSearch ? "Try a different search term." : "Workflow executions will appear here when they run."}
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Execute Workflow Dialog */}
      <Dialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Execute Workflow</DialogTitle>
            <DialogDescription>
              Run the workflow with specific parameters
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="workflow">Workflow</Label>
              <Input
                id="workflow"
                value={executeForm.workflow_name}
                disabled
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="entity-type">Entity Type</Label>
              <Select
                value={executeForm.entity_type}
                onValueChange={(value) =>
                  setExecuteForm({ ...executeForm, entity_type: value })
                }
              >
                <SelectTrigger id="entity-type" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ticket">Ticket</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="universal">Universal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="entity-id">Entity ID</Label>
              <Input
                id="entity-id"
                placeholder="Enter the entity ID to process"
                value={executeForm.entity_id}
                onChange={(e) =>
                  setExecuteForm({ ...executeForm, entity_id: e.target.value })
                }
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowExecuteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={executeWorkflow}
              disabled={loading || !executeForm.entity_id}
            >
              Execute
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Workflow Dialog - Placeholder for now */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Workflow</DialogTitle>
            <DialogDescription>
              Design a new automation workflow
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-12">
            <GitBranch className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Workflow Designer Coming Soon
            </h3>
            <p className="text-muted-foreground text-center">
              The visual workflow designer is under development. For now,
              workflows can be configured through the API.
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Workflow Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Workflow: {selectedWorkflow?.name}</DialogTitle>
            <DialogDescription>
              View and modify workflow template configuration
            </DialogDescription>
          </DialogHeader>
          {selectedWorkflow && (
            <div className="space-y-4">
              <div>
                <Label>Template ID</Label>
                <Input value={selectedWorkflow.id} disabled className="mt-1" />
              </div>
              <div>
                <Label>Name</Label>
                <Input
                  value={selectedWorkflow.name}
                  disabled
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={selectedWorkflow.description}
                  disabled
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Input
                  value={selectedWorkflow.type}
                  disabled
                  className="mt-1"
                />
              </div>
              <div className="flex flex-col items-center justify-center py-8 bg-muted rounded-lg">
                <Edit className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Visual Editor Coming Soon
                </h3>
                <p className="text-muted-foreground text-center">
                  The workflow visual editor is under development. For now, view
                  the workflow visualization or use the API to edit.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Close
            </Button>
            <Button disabled>Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Configure Workflow Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Configure: {selectedWorkflow?.name}</DialogTitle>
            <DialogDescription>
              Workflow settings and parameters
            </DialogDescription>
          </DialogHeader>
          {selectedWorkflow && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={selectedWorkflow.status} disabled>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Trigger Type</Label>
                  <Select value={selectedWorkflow.trigger} disabled>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="api">API</SelectItem>
                      <SelectItem value="schedule">Schedule</SelectItem>
                      <SelectItem value="webhook">Webhook</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center py-8 bg-muted rounded-lg">
                <Settings className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Advanced Configuration Coming Soon
                </h3>
                <p className="text-muted-foreground text-center">
                  Advanced workflow configuration options will be available
                  soon.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowConfigDialog(false)}
            >
              Close
            </Button>
            <Button disabled>Save Configuration</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Execution Detail Dialog */}
      <Dialog open={showExecutionDetail} onOpenChange={setShowExecutionDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Workflow Execution Details
                </DialogTitle>
                <DialogDescription>
                  {selectedExecution?.workflow_name} -{" "}
                  {selectedExecution?.id?.substring(0, 8)}...
                </DialogDescription>
              </div>
              {executionDetail && executionDetail.steps?.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFlowTemplateId(
                      selectedExecution?.workflow_name || null,
                    );
                    setFlowMode("execution");
                    setFlowExecutionSteps(
                      (executionDetail.steps as WorkflowStepData[]) || [],
                    );
                    setFlowExecutionStatus(executionDetail.status || "");
                    setShowFlowDialog(true);
                  }}
                >
                  <GitBranch className="mr-2 h-4 w-4" />
                  View Flow
                </Button>
              )}
            </div>
          </DialogHeader>

          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : executionDetail ? (
            <div className="space-y-6">
              {/* Execution Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Clock className="h-4 w-4" />
                      <span className="text-xs">Duration</span>
                    </div>
                    <p className="text-lg font-semibold">
                      {(executionDetail.total_duration_ms / 1000).toFixed(2)}s
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Hash className="h-4 w-4" />
                      <span className="text-xs">Tokens</span>
                    </div>
                    <p className="text-lg font-semibold">
                      {executionDetail.total_tokens.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <DollarSign className="h-4 w-4" />
                      <span className="text-xs">Cost</span>
                    </div>
                    <p className="text-lg font-semibold">
                      ${executionDetail.total_cost.toFixed(4)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Activity className="h-4 w-4" />
                      <span className="text-xs">Steps</span>
                    </div>
                    <p className="text-lg font-semibold">
                      {executionDetail.step_count}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Execution Info */}
              <Card>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Status:</span>{" "}
                      {getStatusBadge(executionDetail.status)}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Entity:</span>{" "}
                      {executionDetail.entity_type}:{" "}
                      {executionDetail.entity_id.substring(0, 8)}...
                    </div>
                    <div>
                      <span className="text-muted-foreground">Service:</span>{" "}
                      {executionDetail.service_name}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Started:</span>{" "}
                      {new Date(executionDetail.started_at).toLocaleString()}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Completed:</span>{" "}
                      {executionDetail.completed_at
                        ? new Date(
                            executionDetail.completed_at,
                          ).toLocaleString()
                        : "In progress..."}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Step Navigation */}
              {totalSteps > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        Step {currentStepIndex + 1} of {totalSteps}
                      </CardTitle>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setCurrentStepIndex(0)}
                          disabled={currentStepIndex === 0}
                        >
                          <ChevronFirst className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            setCurrentStepIndex(currentStepIndex - 1)
                          }
                          disabled={currentStepIndex === 0}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            setCurrentStepIndex(currentStepIndex + 1)
                          }
                          disabled={currentStepIndex >= totalSteps - 1}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setCurrentStepIndex(totalSteps - 1)}
                          disabled={currentStepIndex >= totalSteps - 1}
                        >
                          <ChevronLast className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {currentStep && (
                      <>
                        {/* Step Header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Bot className="h-5 w-5 text-primary" />
                            <div>
                              <p className="font-medium">
                                {currentStep.agent_name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {currentStep.action_type}
                              </p>
                            </div>
                          </div>
                          {getStatusBadge(currentStep.status)}
                        </div>

                        {/* Step Metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-muted rounded-lg">
                          <div>
                            <span className="text-xs text-muted-foreground">
                              Duration
                            </span>
                            <p className="font-medium">
                              {currentStep.duration_ms
                                ? `${(currentStep.duration_ms / 1000).toFixed(2)}s`
                                : "-"}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">
                              Input Tokens
                            </span>
                            <p className="font-medium">
                              {currentStep.input_tokens?.toLocaleString() ??
                                "-"}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">
                              Output Tokens
                            </span>
                            <p className="font-medium">
                              {currentStep.output_tokens?.toLocaleString() ??
                                "-"}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">
                              Cost
                            </span>
                            <p className="font-medium">
                              {currentStep.cost_estimate
                                ? `$${currentStep.cost_estimate.toFixed(4)}`
                                : "-"}
                            </p>
                          </div>
                        </div>

                        {/* Error Message */}
                        {currentStep.error_message && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-2 text-red-700">
                              <AlertCircle className="h-4 w-4" />
                              <span className="font-medium">Error</span>
                            </div>
                            <p className="mt-1 text-sm text-red-600">
                              {currentStep.error_message}
                            </p>
                          </div>
                        )}

                        {/* Action Data */}
                        {currentStep.action_data &&
                          Object.keys(currentStep.action_data).length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-2">
                                Action Data
                              </p>
                              <pre className="p-3 bg-muted rounded-lg text-xs overflow-x-auto max-h-48">
                                {JSON.stringify(
                                  currentStep.action_data,
                                  null,
                                  2,
                                )}
                              </pre>
                            </div>
                          )}

                        {/* Result */}
                        {currentStep.result &&
                          Object.keys(currentStep.result).length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-2">Result</p>
                              <pre className="p-3 bg-muted rounded-lg text-xs overflow-x-auto max-h-48">
                                {JSON.stringify(currentStep.result, null, 2)}
                              </pre>
                            </div>
                          )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Steps Timeline */}
              {totalSteps > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">All Steps</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {executionDetail.steps.map((step, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentStepIndex(index)}
                          className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${
                            index === currentStepIndex
                              ? "bg-primary/10 border border-primary"
                              : "hover:bg-muted"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 flex items-center justify-center bg-muted rounded-full text-xs font-medium">
                              {index + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm">
                                {step.agent_name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {step.action_type}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {step.total_tokens && (
                              <span className="text-xs text-muted-foreground">
                                {step.total_tokens.toLocaleString()} tokens
                              </span>
                            )}
                            {getStatusBadge(step.status)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Failed to load execution details
              </p>
            </div>
          )}

          <div className="flex justify-end mt-6">
            <Button
              variant="outline"
              onClick={() => setShowExecutionDetail(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Workflow Flow Visualization Dialog */}
      <WorkflowFlowDialog
        open={showFlowDialog}
        onOpenChange={setShowFlowDialog}
        templateId={flowTemplateId}
        mode={flowMode}
        executionSteps={
          flowMode === "execution" ? flowExecutionSteps : undefined
        }
        executionStatus={
          flowMode === "execution" ? flowExecutionStatus : undefined
        }
      />
    </div>
  );
}
